import { useCallback, useEffect, useState } from 'react';
import useFetchSingleShipment from '@/hooks/useFetchSingleShipment';
import useFetchExecutionLegDetail from '@/hooks/useFetchExecutionLegDetail';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useRefreshTelemetrySummary } from '../SensorTimeline/queries';
import 'react-spring-bottom-sheet/dist/style.css';
import arrowLink from '@/assets/images/arrowLink.png';
import theme from '../../../assets/theme';
import { AuthHeader } from '../../../components';
import { GoogleMapsProvider } from '../../../components/GoogleMapsProvider';
import { useSpin } from '../../../components/SpinContext';
import AuthenticateShareShipment from '../AuthenticateShareShipment';
import ShipmentMap, {
  TrackingPoint,
  CourierEventPoint,
  SensorPointWithIndex,
  isCourierEventPoint,
  isSensorPointWithIndex,
} from '../ShipmentMap';
//import MapEventNavigator from './MapEventNavigator';
import {
  AtRisk,
  CommentIcon,
  FileSvgIcon,
  WhiteLogo1,
} from '../../../assets/images';

import { message } from 'antd';
import { GetShipmentAlerts } from '../../../apis/shipment';
import { useToken, capitalize } from '../../../common/helper';
import {
  getFormattedUserTimeZone,
  isZoomlogiSupport,
} from '../../../common/utils/utils';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import useUnauthorize from '@/hooks/useUnauthorize';
import useUserDateTimeFormat from '@/hooks/useUserDateTimeFormat';
import MobileBottomSheet from './MobileBottomSheet';
import DesktopShipmentDrawer from './DesktopShipmentDrawer';
import { useSharePageData } from '@/hooks/useSharePageData';
import { ShipmentAlertData, SingleShipmentDataExtended } from '@/apis/types';
import { DisplayTimeSelection } from './types';
import { TimeFormatResult } from '../partials/CoC_CT';
import MapPointsCards from './MapPointsCards';
import { normalizeShareTimezone } from './normalizeShareTimezone';
import { getSensorPointId } from '../mapSensorUtils';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

export interface CardVisibilityState {
  showCard: boolean;
  isOpen: boolean;
  name: string;
}

function MapExternalView() {
  const navigate = useNavigate();
  const { showSpin, hideSpin } = useSpin();
  const { token, removeToken, decodedUserToken } = useToken();
  const { unauthorize } = useUnauthorize();
  const { formatDateTime } = useUserDateTimeFormat();
  const { id, uuid } = useParams();
  const refreshTelemetrySummary = useRefreshTelemetrySummary();
  const location = useLocation();
  const isSharePage = location.pathname.includes('/share');
  const [authShipment, setAuthShipment] =
    useState<SingleShipmentDataExtended | null>(null);
  const [authAlertsData, setAuthAlertsData] = useState<
    ShipmentAlertData[] | null
  >(null);
  const [showAlertForm, setShowAlertForm] = useState(false);
  const [pointState, setPointState] = useState(null);
  const [showAlerts, setShowAlerts] = useState(false);
  const [productDetails, setProductDetails] = useState(false);
  const [alertIndex, setAlertIndex] = useState(0);
  const isVisible = false;
  const isExpanded = true;
  const [openDropdown, setOpenDropdown] = useState(false);
  const [showPointCard, setShowPointCard] = useState(false);
  const [currentPointSelected, setCurrentPointSelected] = useState(-1);
  const urlParams = new URLSearchParams(location.search);
  const legType = urlParams.get('leg_type') || urlParams.get('legType') || 'CL';
  const [cardVisibilityState, setCardVisibilityState] =
    useState<CardVisibilityState>({
      showCard: false,
      isOpen: false,
      name: '',
    });

  const handleCardActions = (
    showCard: boolean,
    isOpen: boolean,
    name: string
  ) => {
    setCardVisibilityState({
      showCard: showCard,
      isOpen: isOpen,
      name: name,
    });
  };

  const decodedToken = decodedUserToken();
  const companyName = decodedToken?.company_name;

  const startAlertsInterval = true;

  const [displayTime, setDisplayTime] = useState<DisplayTimeSelection>({
    type: 'preset',
    value: 'local_scan_time',
  });

  const getShipmentAlerts = useCallback(
    (showSpinner?: boolean) => {
      if (isZoomlogiSupport(companyName)) return;
      if (showSpinner) showSpin();

      GetShipmentAlerts(token, id)
        .then((res) => {
          const data = res?.data ?? null;
          if (data?.data?.is_success) {
            setAuthAlertsData(data?.data?.data ?? []);
          } else {
            message.error(data?.data?.message);
          }
        })
        .catch((error) => unauthorize(error))
        .finally(() => showSpinner && hideSpin());
    },
    [companyName, showSpin, token, id, unauthorize, hideSpin]
  );

  // Phase 1: minimal — vitals + metadata, populates ShipmentVitals_CT immediately.
  const { shipmentData, refetchShipment } = useFetchSingleShipment(
    isSharePage || legType === 'EL' ? undefined : id,
    true,
    'minimal'
  );
  // Phase 2: full — GPS track for the map, fires after Phase 1 resolves.
  const monitorShipmentId =
    shipmentData && !isSharePage && legType === 'CL' ? id : undefined;
  const { shipmentData: monitorData } = useFetchSingleShipment(
    monitorShipmentId,
    false,
    'full'
  );

  useEffect(() => {
    if (!shipmentData) return;
    setAuthShipment((prev) => ({
      ...shipmentData,
      shipment_monitor: shipmentData.shipment_monitor?.length
        ? shipmentData.shipment_monitor
        : (prev?.shipment_monitor ?? []),
      shipment_polyline:
        shipmentData.shipment_polyline ?? prev?.shipment_polyline,
      legs_info: shipmentData.legs_info ?? prev?.legs_info,
    }));
  }, [shipmentData]);

  useEffect(() => {
    if (!monitorData?.shipment_monitor?.length) return;
    setAuthShipment((prev) =>
      prev
        ? {
            ...prev,
            shipment_monitor: monitorData.shipment_monitor,
            shipment_polyline:
              monitorData.shipment_polyline ?? prev.shipment_polyline,
            legs_info: monitorData.legs_info ?? prev.legs_info,
          }
        : prev
    );
  }, [monitorData]);

  const {
    passwordRequired: sharePasswordRequired,
    setPasswordRequired: setSharePasswordRequired,
    shipment: shareShipment,
    alertsData: shareAlertsData,
    refetch: refetchSharePageData,
  } = useSharePageData({
    id,
    uuid,
    enabled: isSharePage,
    pollMs: 30000,
    onUnauthorized: () => {
      removeToken();
      navigate('/login');
    },
    displayTime,
    onDefaultDisplayTime: setDisplayTime,
  });

  useEffect(() => {
    if (token && !isSharePage && legType === 'CL') {
      getShipmentAlerts(true);
    }
  }, [token, id, isSharePage, getShipmentAlerts, legType]);

  const isELMap = !isSharePage && legType === 'EL' && Boolean(id);
  const { detail: elMapDetail, refetchExecutionLegDetail } =
    useFetchExecutionLegDetail(isELMap ? id : null, isELMap, true);

  useEffect(() => {
    if (!elMapDetail || legType !== 'EL') return;
    const formattedRes = {
      ...elMapDetail,
      leg_type: 'EL' as const,
    };
    setAuthShipment(formattedRes as unknown as SingleShipmentDataExtended);
  }, [elMapDetail, legType]);

  useEffect(() => {
    if (!startAlertsInterval || isSharePage) return;
    const intervalId = setInterval(() => {
      if (token) {
        if (legType === 'CL') {
          refetchShipment(false, true);
          getShipmentAlerts(false);
          if (id) {
            const _ = refreshTelemetrySummary(id);
          }
        } else {
          refetchExecutionLegDetail(false);
        }
      }
    }, 30000);
    return () => clearInterval(intervalId);
  }, [
    token,
    id,
    startAlertsInterval,
    isSharePage,
    refetchShipment,
    getShipmentAlerts,
    refetchExecutionLegDetail,
    legType,
    refreshTelemetrySummary,
  ]);

  const effectiveShipment: Partial<SingleShipmentDataExtended> = isSharePage
    ? shareShipment
    : (authShipment ?? {});
  const effectiveAlertsData = isSharePage ? shareAlertsData : authAlertsData;
  const effectivePasswordRequired = isSharePage ? sharePasswordRequired : false;

  // Track selected event for map highlighting (declared before useCallback that uses it)
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [selectedPointType, setSelectedPointType] = useState<string | null>(
    null
  );
  // Set only by Previous/Next navigation; a fresh object each time so that
  // re-selecting the same coordinates still recenters the map.
  const [focusPoint, setFocusPoint] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  const updateTrackingInfo = useCallback(
    (
      point: TrackingPoint | CourierEventPoint | SensorPointWithIndex,
      index: number
    ): void => {
      // Handle courier events (type=2)
      if (isCourierEventPoint(point)) {
        setSelectedEventId(point.id);
        setShowPointCard(true);
        setSelectedPointType('courier');
        setCurrentPointSelected(index);
        setPointState({
          action: point.action,
          location: point.location,
          timestamp: point.timestamp,
          handler: point.handler,
          temperature: '',
          temperature_updated: null,
          temperature_age_seconds: null,
          battery: '',
          battery_updated: null,
          battery_age_seconds: null,
          light: '',
          humidity: '',
          tilt: '',
          pressure: '',
          shock_value: '',
          cell_signal: '',
          lat_updated: point.timestamp || '',
        });
        return;
      }

      // Handle sensor points with index (from detail view)
      if (isSensorPointWithIndex(point)) {
        setSelectedPointType('sensor');
        setSelectedEventId(getSensorPointId(point));
        setShowPointCard(true);
        setCurrentPointSelected(index);
        setPointState({
          lat_updated: point.lat_updated || '',
          // `??` rather than `||`: a reading of 0 is real and must survive.
          temperature: point.temperature ?? '',
          temperature_updated: point.temperature_updated ?? null,
          temperature_age_seconds: point.temperature_age_seconds ?? null,
          battery: point.battery ?? '',
          battery_updated: point.battery_updated ?? null,
          battery_age_seconds: point.battery_age_seconds ?? null,
          light: point.light ?? '',
          humidity: point.humidity ?? '',
          tilt: point.tilt ?? '',
          pressure: point.pressure ?? '',
          shock_value: point.shock_value ?? '',
          cell_signal: point.cell_signal ?? '',
        });
        return;
      }

      // Handle regular tracking points (from summary view polylines)
      // These don't have an index, so we just update the point state
      const trackingPoint = point as TrackingPoint;
      setPointState({
        lat_updated: trackingPoint.lat_updated ?? '',
      });
      setShowPointCard(false);
      setCurrentPointSelected(-1);
      // Clear selection for summary view clicks (no specific event selected)
      setSelectedEventId(null);
    },
    [
      setSelectedEventId,
      setPointState,
      setShowPointCard,
      setSelectedPointType,
      setCurrentPointSelected,
    ]
  );

  // Handler for MapEventNavigator selection
  // const handleEventNavigatorSelect = (event: {
  //   id: string;
  //   type: 'sensor' | 'courier';
  //   latitude: number;
  //   longitude: number;
  //   timestamp?: string;
  //   temperature?: string;
  // }) => {
  //   if (event.type === 'sensor') {
  //     // Find the full sensor data
  //     const monitors = effectiveShipment?.shipment_monitor || [];
  //     const sensorPrefix = 'sensor-';
  //     if (!event.id.startsWith(sensorPrefix)) {
  //       return;
  //     }
  //     const indexString = event.id.slice(sensorPrefix.length);
  //     const index = parseInt(indexString, 10);
  //     if (Number.isNaN(index) || index < 0 || index >= monitors.length) {
  //       return;
  //     }
  //     const point = monitors[index];
  //     if (!point) {
  //       return;
  //     }
  //     setSelectedEventId(event.id);
  //     setPointState({
  //       temperature: point.temperature ?? '',
  //       battery: point.battery ?? '',
  //       light: point.light ?? '',
  //       humidity: point.humidity ?? '',
  //       tilt: point.tilt ?? '',
  //       pressure: point.pressure ?? '',
  //       lat_updated: point.lat_updated ?? '',
  //     });
  //   } else {
  //     // Courier event - clear sensor vitals
  //     setSelectedEventId(event.id);
  //     setPointState({
  //       temperature: '',
  //       battery: '',
  //       light: '',
  //       humidity: '',
  //       tilt: '',
  //       pressure: '',
  //       lat_updated: event.timestamp || '',
  //     });
  //   }
  // };

  // useEffect(() => {
  //   const monitors = effectiveShipment?.shipment_monitor;
  //   if (Array.isArray(monitors) && monitors.length > 0) {
  //     debugger
  //     const trackingPoint = monitors[monitors.length - 1];
  //     setPointState({
  //       temperature: trackingPoint.temperature ?? '',
  //       battery: trackingPoint.battery ?? '',
  //       light: trackingPoint.light ?? '',
  //       humidity: trackingPoint.humidity ?? '',
  //       tilt: trackingPoint.tilt ?? '',
  //       pressure: trackingPoint.pressure ?? '',
  //       lat_updated: trackingPoint.lat_updated ?? '',
  //     });
  //   }
  // }, [effectiveShipment]);

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const updateDimensions = () => setIsMobile(window.innerWidth <= 768);
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    window.addEventListener('orientationchange', updateDimensions);
    return () => {
      window.removeEventListener('resize', updateDimensions);
      window.removeEventListener('orientationchange', updateDimensions);
    };
  }, []);

  const [showFull, setShowFull] = useState(false);

  const isTimeZone = decodedUserToken()?.time_zone_enable;
  const getFormattedTime = (
    ts: string,
    preferredTz?: string
  ): TimeFormatResult & { selectedTimezone: string; currentTime: string } => {
    let userTz: string | undefined =
      normalizeShareTimezone(preferredTz) ??
      (isSharePage
        ? displayTime.value === 'local_scan_time'
          ? normalizeShareTimezone(preferredTz) || dayjs.tz.guess()
          : normalizeShareTimezone(effectiveShipment?.timezone) ||
            dayjs.tz.guess()
        : undefined);

    if (!userTz && !isSharePage && isTimeZone) {
      const tzString = getFormattedUserTimeZone();
      const match = tzString.match(/ ([A-Za-z_/]+)$/);
      userTz = match ? match[1] : dayjs.tz.guess();
    }

    const format = 'YYYY-MM-DD HH:mm:ss';
    let targetForDiff = dayjs(ts, format).local();
    let nowInTz = dayjs();
    try {
      if (userTz) {
        targetForDiff = dayjs.tz(ts, format, userTz);
        nowInTz = dayjs().tz(userTz);
      }
    } catch {
      userTz = dayjs.tz.guess();
      targetForDiff = dayjs(ts, format).local();
      nowInTz = dayjs();
    }

    const fullFormatted = formatDateTime(targetForDiff, { includeTime: true });

    const diffSeconds = nowInTz.diff(targetForDiff, 'second');
    const absMin = Math.floor(Math.abs(diffSeconds) / 60);
    const absHr = Math.floor(absMin / 60);
    const absDay = Math.floor(absHr / 24);

    let relative: string;
    if (absDay >= 1) {
      relative =
        diffSeconds >= 0
          ? `${absDay} day${absDay > 1 ? 's' : ''} ago`
          : `${absDay} day${absDay > 1 ? 's' : ''}`;
    } else if (absHr >= 1) {
      relative =
        diffSeconds >= 0
          ? `${absHr} hr${absHr > 1 ? 's' : ''} ago`
          : `${absHr} hr${absHr > 1 ? 's' : ''}`;
    } else if (absMin >= 1) {
      relative =
        diffSeconds >= 0
          ? `${absMin} minute${absMin > 1 ? 's' : ''} ago`
          : `${absMin} minute${absMin > 1 ? 's' : ''}`;
    } else {
      relative = 'just now';
    }

    const currentTime = formatDateTime(nowInTz, { includeTime: true });
    const selectedTimezone = userTz || dayjs.tz.guess();

    return {
      full: fullFormatted,
      relative,
      selectedTimezone,
      currentTime,
    };
  };

  const downloadFile = async (fileUrl?: string, fileName = 'file') => {
    if (!fileUrl) {
      message.error('File not found!');
      return;
    }
    const extension = fileName?.split('.').pop()?.toLowerCase();
    const previewableTypes = [
      'pdf',
      'txt',
      'csv',
      'json',
      'xml',
      'jpg',
      'jpeg',
      'png',
      'gif',
      'webp',
      'svg',
    ];

    if (extension && previewableTypes.includes(extension)) {
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    }
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error('Network error while downloading');
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error(error);
    }
  };

  const [halfHeight, setHalfHeight] = useState<Record<string | number, number>>(
    {}
  );
  const handleSetHalfHeight = useCallback(
    (id: string | number, height: number) => {
      setHalfHeight((prev) => ({ ...prev, [id]: height }));
    },
    []
  );

  const [currentView] = useState<'summary' | 'detail'>('detail');
  //const handleCurrentView = (val: 'summary' | 'detail') => setCurrentView(val);
  const handleClose = () => setShowAlertForm(false);

  const HIDDEN_STATUSES = ['delivered', 'archived'];

  const reportIssueConfig = effectiveShipment?.report_issue_config;
  const addressOptionAvailable =
    (reportIssueConfig?.address_update_enabled ?? true) &&
    !!effectiveShipment?.to_location?.address?.trim();
  const timeOptionAvailable =
    (reportIssueConfig?.delivery_time_enabled ?? true) &&
    !!effectiveShipment?.scheduled_delivery_date;

  const canShowReportIssueCTA =
    !HIDDEN_STATUSES.includes(effectiveShipment?.status?.toLowerCase()) &&
    (addressOptionAvailable || timeOptionAvailable);

  return (
    <>
      {effectivePasswordRequired ? (
        <AuthenticateShareShipment
          setShowAlerts={setShowAlerts}
          setProductDetails={setProductDetails}
          setPasswordRequired={setSharePasswordRequired}
          uuid={uuid}
        />
      ) : (
        <>
          <AuthHeader
            bgColor={theme.primary}
            color='#fff'
            logo={WhiteLogo1}
            isMapPage
            isSharePage={isSharePage}
            currentView={currentView}
            returnShipmentId={id}
            legType={legType}
          />

          <div className='map-container' style={{ top: '0px' }}>
            <GoogleMapsProvider>
              <ShipmentMap
                shipment={effectiveShipment}
                currentView={currentView}
                onPointClicked={updateTrackingInfo}
                height={'calc(100vh - 56px)'}
                isLargeScreen
                selectedPointId={selectedEventId}
                focusPoint={focusPoint}
                // Zoom 2 is the lowest level with no blank bands on phones.
                minZoom={isSharePage && isMobile ? 2 : undefined}
                isMobile={isSharePage && isMobile}
              />
              {/* Map Event Navigator for arrow navigation (only in detail view) */}
              {/* {currentView === 'detail' && (
                <MapEventNavigator
                  shipment={effectiveShipment}
                  selectedEventId={selectedEventId}
                  onEventSelect={handleEventNavigatorSelect}
                />
              )} */}
            </GoogleMapsProvider>

            {!isMobile && (
              <>
                {showPointCard ? (
                  <MapPointsCards
                    pointState={pointState}
                    setPointState={setPointState}
                    setShowPointCard={setShowPointCard}
                    shipment={effectiveShipment}
                    currentPointSelected={currentPointSelected}
                    setCurrentPointSelected={setCurrentPointSelected}
                    selectedPointType={selectedPointType}
                    setSelectedPointType={setSelectedPointType}
                    selectedEventId={selectedEventId}
                    setSelectedEventId={setSelectedEventId}
                    onRecenter={setFocusPoint}
                  />
                ) : (
                  <DesktopShipmentDrawer
                    openDropdown={openDropdown}
                    setOpenDropdown={setOpenDropdown}
                    displayTime={displayTime}
                    onChangeDisplayTime={setDisplayTime}
                    isVisible={isVisible}
                    isExpanded={isExpanded}
                    showAlertForm={showAlertForm}
                    setShowAlertForm={setShowAlertForm}
                    cardVisibilityState={cardVisibilityState}
                    handleCardActions={handleCardActions}
                    canShowReportIssueCTA={canShowReportIssueCTA}
                    handleClose={handleClose}
                    isSharePage={isSharePage}
                    showAlerts={showAlerts}
                    alertsData={effectiveAlertsData ?? []}
                    alertIndex={alertIndex}
                    setAlertIndex={setAlertIndex}
                    uuid={uuid}
                    shipment={effectiveShipment}
                    shipmentId={legType === 'CL' || isSharePage ? id : null}
                    legType={legType}
                    productDetails={productDetails}
                    pointState={pointState}
                    capitalize={capitalize}
                    getFormattedTime={getFormattedTime}
                    downloadFile={downloadFile}
                    halfHeight={halfHeight}
                    showFull={showFull}
                    setShowFull={setShowFull}
                    handleSetHalfHeight={handleSetHalfHeight}
                    arrowLink={arrowLink}
                    CommentIcon={CommentIcon}
                    AtRisk={AtRisk}
                    FileSvgIcon={FileSvgIcon}
                    fetchSingleShipmentData={refetchShipment}
                    refetchSharePageData={refetchSharePageData}
                  />
                )}
              </>
            )}

            {isMobile && (
              <MobileBottomSheet
                openDropdown={openDropdown}
                setOpenDropdown={setOpenDropdown}
                displayTime={displayTime}
                onChangeDisplayTime={setDisplayTime}
                open
                handleClose={handleClose}
                isSharePage={isSharePage}
                showAlertForm={showAlertForm}
                setShowAlertForm={setShowAlertForm}
                cardVisibilityState={cardVisibilityState}
                handleCardActions={handleCardActions}
                canShowReportIssueCTA={canShowReportIssueCTA}
                showAlerts={showAlerts}
                alertsData={effectiveAlertsData ?? []}
                alertIndex={alertIndex}
                setAlertIndex={setAlertIndex}
                uuid={uuid}
                shipment={effectiveShipment}
                shipmentId={legType === 'CL' || isSharePage ? id : null}
                legType={legType}
                productDetails={productDetails}
                pointState={pointState}
                capitalize={capitalize}
                getFormattedTime={getFormattedTime}
                downloadFile={downloadFile}
                halfHeight={halfHeight}
                setHalfHeight={setHalfHeight}
                showFull={showFull}
                setShowFull={setShowFull}
                arrowLink={arrowLink}
                CommentIcon={CommentIcon}
                AtRisk={AtRisk}
                FileSvgIcon={FileSvgIcon}
                showPointCard={showPointCard}
                setPointState={setPointState}
                setShowPointCard={setShowPointCard}
                currentPointSelected={currentPointSelected}
                setCurrentPointSelected={setCurrentPointSelected}
                selectedPointType={selectedPointType}
                setSelectedPointType={setSelectedPointType}
                selectedEventId={selectedEventId}
                setSelectedEventId={setSelectedEventId}
                onRecenter={setFocusPoint}
                refetchSharePageData={refetchSharePageData}
              />
            )}
          </div>
        </>
      )}
    </>
  );
}

export default MapExternalView;
